import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageThumb from "@/components/ImageThumb";
import DescriptionTab from "@/components/DescriptionTab";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { TrendingDown, Heart, Users, Sparkles, Eye, Tag, Trash2, Lock, Image as ImageIcon } from "lucide-react";
import AuthBadge from "@/components/AuthBadge";
import PhotoUpload from "@/components/PhotoUpload";
import TrustPanel from "@/components/TrustPanel";
import SellerPlanPanel from "@/components/SellerPlanPanel";
import SellerSales from "@/components/SellerSales";
import { garmentTypesFor } from "@/lib/taxonomy";
import BumpTierPicker from "@/components/BumpTierPicker";

const expiryLabelD = (iso) => {
  if (!iso) return "";
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "expired";
  const h = Math.ceil(ms / 3.6e6);
  return h > 24 ? `${Math.ceil(h / 24)}d left` : `${h}h left`;
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [liked, setLiked] = useState([]);
  const [mine, setMine] = useState([]);
  const [demoStatus, setDemoStatus] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [reducing, setReducing] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [expandedBuyers, setExpandedBuyers] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [offerPrices, setOfferPrices] = useState({});
  const [proofFor, setProofFor] = useState(null);
  const [proofPaths, setProofPaths] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [inCounter, setInCounter] = useState({});

  const load = async () => {
    const [l, m, inc] = await Promise.all([
      api.get("/seller/liked-items"),
      api.get("/seller/my-listings"),
      api.get("/seller/offers/incoming"),
    ]);
    setLiked(l.data); setMine(m.data); setIncoming(inc.data || []);
  };
  useEffect(() => { load(); }, []);

  const removeListing = async (l) => {
    if (l.sold) { toast.error("Sold listings can't be deleted."); return; }
    if (!window.confirm(`Delete "${l.title}"? This permanently removes it from the site.`)) return;
    try {
      await api.delete(`/listings/${l.id}`);
      setMine((prev) => prev.filter((x) => x.id !== l.id));
      toast.success("Listing deleted.");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const editListing = (l) => nav(`/sell?edit=${l.id}`);


  const loadDemoStatus = () => api.get("/demo-status").then((r) => setDemoStatus(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "admin") loadDemoStatus(); }, [user]);

  const [privReqs, setPrivReqs] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const loadPrivReqs = () => api.get("/private/requests").then((r) => setPrivReqs(r.data)).catch(() => {});
  useEffect(() => { loadPrivReqs(); }, []);

  const createInviteLink = async () => {
    try {
      const { data } = await api.post("/private/invite-link");
      const url = data.url || `${window.location.origin}/seller/${data.seller_id}/private?invite=${data.token}`;
      setInviteLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Invite link copied — valid for 24 hours"); }
      catch { toast.success("Invite link ready — valid for 24 hours"); }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const respondReq = async (rid, accept) => {
    try {
      await api.post(`/private/requests/${rid}/respond`, { accept });
      toast.success(accept ? "Access granted" : "Request declined");
      loadPrivReqs();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const inviteFavorites = async () => {
    setInviting(true);
    try {
      const { data } = await api.post("/private/invite");
      toast.success(data.invited > 0
        ? `Invited ${data.invited} favourite${data.invited === 1 ? "" : "s"} — access lasts 24 hours`
        : "No favourites to invite yet");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const clearDemos = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/demos/clear");
      toast.success(`Removed ${data.cleared} demo placeholder${data.cleared === 1 ? "" : "s"}`);
      loadDemoStatus();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  const retag = async (id, garment_type) => {
    if (!garment_type) return;
    try {
      await api.patch(`/listings/${id}/garment-type`, { garment_type });
      setMine((s) => s.map((x) => (x.id === id ? { ...x, garment_type } : x)));
      toast.success("Categorized — it'll now show under that category");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const respondIncoming = async (id, action, counter_price) => {
    try {
      await api.post(`/offers/${id}/respond`, { action, counter_price });
      toast.success(action === "accept" ? "Offer accepted — the buyer can now check out" : action === "counter" ? "Counter sent to the buyer" : "Offer declined");
      setInCounter(s => ({ ...s, [id]: undefined }));
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const doReduce = async (lid) => {
    const p = parseFloat(newPrice);
    if (!p || p <= 0) { toast.error("Enter a valid price"); return; }
    await api.patch(`/listings/${lid}/reduce`, null, { params: { new_price: p } });
    toast.success("Price reduced — buyers who liked it will be notified");
    setReducing(null); setNewPrice("");
    load();
  };

  const openBuyers = async (lid) => {
    if (expandedBuyers === lid) { setExpandedBuyers(null); return; }
    const { data } = await api.get(`/seller/likers/${lid}`);
    setBuyers(data); setExpandedBuyers(lid);
  };

  const [featureFor, setFeatureFor] = useState(null);
  const [boostTiers, setBoostTiers] = useState(null);
  useEffect(() => { api.get("/pricing").then(r => setBoostTiers(r.data?.boost?.home_bump_tiers)).catch(() => {}); }, []);

  const sendOffer = async (lid, buyer_id) => {
    const p = parseFloat(offerPrices[buyer_id]);
    if (!p || p <= 0) { toast.error("Enter a valid offer price"); return; }
    try {
      await api.post("/seller/offers", { listing_id: lid, buyer_id, offer_price: p });
      toast.success("Private offer sent to buyer");
      const { data } = await api.get(`/seller/likers/${lid}`);
      setBuyers(data);
      setOfferPrices(s => ({ ...s, [buyer_id]: "" }));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const rescreen = async (lid) => {
    if (!proofPaths.length) { toast.error("Add at least one new proof photo"); return; }
    try {
      await api.post(`/listings/${lid}/rescreen`, { proof_paths: proofPaths });
      toast.success("Re-submitted — screening again now");
      setProofFor(null); setProofPaths([]);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="dashboard-page" className="max-w-[1400px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6">
        <div className="label-eyebrow">Seller HQ</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Dashboard.</h1>
      </div>

      <TrustPanel />
      <SellerPlanPanel />

      <section className="mt-10 hairline p-6" data-testid="private-manage-panel" style={{ borderColor: "#1E3A2B" }}>
        <div className="flex items-center gap-2">
          <Lock size={15} style={{ color: "#1E3A2B" }} />
          <span className="label-eyebrow" style={{ color: "#1E3A2B" }}>Private collection</span>
        </div>
        <h2 className="font-display text-2xl tracking-tight mt-2">Your private listing.</h2>
        <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
          List up to 20 items only your approved favourites can see. Approve join requests below, or invite everyone
          who's favourited you with a 24-hour pass. <strong>Sellers pay a 3% fee on private sales.</strong>
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link to="/sell" className="btn-primary text-[0.68rem]" data-testid="add-private-btn">
            <Lock size={13} /> Add a private item
          </Link>
          <button onClick={inviteFavorites} disabled={inviting} className="btn-outline text-[0.68rem]" data-testid="invite-favorites-btn">
            {inviting ? "Inviting…" : "Invite my favourites (24h)"}
          </button>
          <button onClick={createInviteLink} className="btn-outline text-[0.68rem]" data-testid="create-invite-link-btn">
            <Lock size={13} /> Create shareable link (24h)
          </button>
        </div>
        {inviteLink && (
          <div className="mt-3 flex items-center gap-2" data-testid="invite-link-box">
            <input readOnly value={inviteLink} data-testid="invite-link-input"
              onFocus={(e) => e.target.select()}
              className="flex-1 hairline px-3 py-2 text-xs bg-neutral-50 outline-none" />
            <button onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Copied"); }}
              className="btn-klein text-[0.6rem]" data-testid="copy-invite-link-btn">Copy</button>
          </div>
        )}
        {privReqs.length > 0 && (
          <div className="mt-6" data-testid="private-requests">
            <div className="label-eyebrow mb-2">Access requests ({privReqs.length})</div>
            <ul className="space-y-2">
              {privReqs.map((r) => (
                <li key={r.id} className="hairline p-3 flex items-center justify-between gap-3 bg-white" data-testid={`priv-req-${r.id}`}>
                  <div className="text-sm">
                    <span className="font-medium">{r.user_name}</span>
                    <span className="text-neutral-400 text-xs ml-2">{r.user_email}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => respondReq(r.id, true)} className="btn-klein text-[0.6rem]" data-testid={`priv-accept-${r.id}`}>Accept</button>
                    <button onClick={() => respondReq(r.id, false)} className="btn-outline text-[0.6rem]" data-testid={`priv-decline-${r.id}`}>Decline</button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {user?.role === "admin" && demoStatus && demoStatus.total_demos > 0 && (
        <section className="mt-10 hairline p-6" data-testid="demo-cleanup-panel" style={{ borderColor: "#E11D48" }}>
          <div className="flex items-center gap-2">
            <Trash2 size={15} style={{ color: "#E11D48" }} />
            <span className="label-eyebrow" style={{ color: "#E11D48" }}>Launch tools · Admin</span>
          </div>
          <h2 className="font-display text-2xl tracking-tight mt-2">Clear the demo placeholders.</h2>
          <p className="text-sm text-neutral-600 mt-1 max-w-2xl">
            <strong>{demoStatus.total_demos}</strong> demo item{demoStatus.total_demos === 1 ? "" : "s"} still showing as SOLD OUT
            {demoStatus.categories_with_demos?.length > 0 && <> across {demoStatus.categories_with_demos.join(", ")}</>}.
            They clear automatically on {demoStatus.cutoff}, or once a category reaches 5 real listings — or remove them all now.
          </p>
          <button onClick={clearDemos} disabled={clearing} data-testid="clear-demos-btn"
            className="btn-primary mt-4" style={{ background: "#E11D48", borderColor: "#E11D48" }}>
            <Trash2 size={14} /> {clearing ? "Removing…" : "Remove all demo items now"}
          </button>
        </section>
      )}

      {incoming.length > 0 && (
        <section className="mt-10" data-testid="incoming-offers">
          <div className="flex items-baseline justify-between hairline-b pb-3">
            <h2 className="font-display text-2xl">Offers from buyers</h2>
            <span className="label-eyebrow">Accept, decline, or counter — buyers have 48h</span>
          </div>
          <ul className="mt-6 divide-y divide-black/10">
            {incoming.map(o => (
              <li key={o.id} className="py-4 flex flex-wrap items-center gap-4" data-testid={`incoming-offer-${o.id}`}>
                <Link to={`/product/${o.listing.id}`} className="w-16 h-16 hairline overflow-hidden shrink-0">
                  <ImageThumb path={o.listing.image_paths?.[0]} className="w-full h-full object-cover"/>
                </Link>
                <div className="min-w-0 flex-1">
                  <div className="font-display truncate">{o.listing.title}</div>
                  <div className="text-xs text-neutral-500 mt-1">{o.buyer_name} offered · {expiryLabelD(o.expires_at)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-neutral-400 line-through">${Number(o.original_price).toFixed(2)}</div>
                  <div className="font-display text-base" style={{color:"#2563EB"}} data-testid={`incoming-price-${o.id}`}>${Number(o.offer_price).toFixed(2)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-klein text-[0.68rem]" onClick={()=>respondIncoming(o.id, "accept")} data-testid={`incoming-accept-${o.id}`}>Accept</button>
                  <button className="btn-outline text-[0.68rem]" onClick={()=>setInCounter(s=>({...s,[o.id]: s[o.id]===undefined?"":undefined}))} data-testid={`incoming-counter-${o.id}`}>Counter</button>
                  <button className="chip" onClick={()=>respondIncoming(o.id, "decline")} data-testid={`incoming-decline-${o.id}`}>Decline</button>
                </div>
                {inCounter[o.id] !== undefined && (
                  <div className="w-full flex items-center gap-2 sm:pl-20" data-testid={`incoming-counter-box-${o.id}`}>
                    <input type="number" value={inCounter[o.id]} onChange={(e)=>setInCounter(s=>({...s,[o.id]:e.target.value}))} placeholder="Your counter price" className="hairline p-2 text-sm w-44" data-testid={`incoming-counter-input-${o.id}`}/>
                    <button className="btn-primary text-[0.68rem]" onClick={()=>respondIncoming(o.id, "counter", parseFloat(inCounter[o.id]))} data-testid={`incoming-counter-send-${o.id}`}>Send counter</button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-10">
        <div className="flex items-baseline justify-between hairline-b pb-3">
          <h2 className="font-display text-2xl">Your liked items</h2>
          <span className="label-eyebrow">Offer a reduced price to interested buyers</span>
        </div>
        {liked.length === 0 ? (
          <div className="hairline p-10 mt-6 text-center">
            <div className="label-eyebrow">No likes yet</div>
            <p className="mt-2 text-sm text-neutral-500">List distinctive pieces to attract likes. Head to <Link to="/sell" className="underline">Sell</Link>.</p>
          </div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {liked.map(({listing, like_count}) => (
              <div key={listing.id} data-testid={`liked-item-${listing.id}`} className="hairline p-4">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 hairline overflow-hidden shrink-0">
                    <ImageThumb path={listing.image_paths?.[0]} className="w-full h-full object-cover"/>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display truncate">{listing.title}</div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
                      <Heart size={12} style={{color:"#2563EB"}}/>
                      <span data-testid={`like-count-${listing.id}`}>{like_count} interested buyer{like_count>1?"s":""}</span>
                    </div>
                    <div className="mt-1 font-display">${Number(listing.price).toFixed(2)}</div>
                  </div>
                </div>
                {reducing === listing.id ? (
                  <div className="mt-4 flex gap-2">
                    <input type="number" step="0.01" min="0.5" placeholder="New price"
                      className="flex-1 hairline p-2 text-sm" value={newPrice} onChange={e=>setNewPrice(e.target.value)}
                      data-testid={`new-price-${listing.id}`}/>
                    <button className="btn-klein text-[0.68rem]" onClick={()=>doReduce(listing.id)} data-testid={`confirm-reduce-${listing.id}`}>Offer</button>
                    <button className="chip" onClick={()=>{setReducing(null); setNewPrice("");}}>Cancel</button>
                  </div>
                ) : (
                  <>
                    <div className="mt-4">
                      <DescriptionTab description={listing.description} testid={`desc-tab-${listing.id}`}/>
                    </div>
                    <button onClick={()=>{setReducing(listing.id); setNewPrice((listing.price*0.9).toFixed(2));}}
                      className="btn-klein w-full mt-3" data-testid={`reduce-btn-${listing.id}`}>
                      <TrendingDown size={14}/> Offer reduced price to all
                    </button>
                    <button onClick={()=>openBuyers(listing.id)}
                      className="btn-outline w-full mt-2" data-testid={`buyers-btn-${listing.id}`}>
                      <Users size={14}/> {expandedBuyers===listing.id ? "Hide interested buyers" : "Send a private offer"}
                    </button>
                  </>
                )}
                {expandedBuyers === listing.id && (
                  <div className="mt-3 border-t border-black/10 pt-3 space-y-3" data-testid={`buyers-panel-${listing.id}`}>
                    {buyers.length === 0 ? (
                      <div className="text-xs text-neutral-500">No interested buyers yet.</div>
                    ) : buyers.map(b => (
                      <div key={b.buyer_id} data-testid={`buyer-row-${b.buyer_id}`} className="text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{b.buyer_name}</span>
                          {b.offer && (
                            <span className="text-[0.6rem] uppercase tracking-wider whitespace-nowrap"
                              style={{color: b.offer.status==="accepted" ? "#16A34A" : "#2563EB"}}
                              data-testid={`offer-status-${b.buyer_id}`}>
                              {b.offer.status} · ${Number(b.offer.offer_price).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="text-[0.66rem] text-neutral-500 mt-0.5">
                          likes: {b.liked_color || "—"} · {b.liked_pattern || "—"} · {b.liked_style || "—"}
                        </div>
                        <div className="mt-1.5 flex gap-2">
                          <input type="number" step="0.01" min="0.5" placeholder="Your offer $"
                            className="flex-1 hairline p-1.5 text-xs" value={offerPrices[b.buyer_id] || ""}
                            onChange={e=>setOfferPrices(s=>({...s,[b.buyer_id]:e.target.value}))}
                            data-testid={`offer-input-${b.buyer_id}`}/>
                          <button className="btn-klein text-[0.62rem]" onClick={()=>sendOffer(listing.id, b.buyer_id)}
                            data-testid={`send-offer-${b.buyer_id}`}>Send</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {mine.filter((l) => l.product_type === "apparel" && !l.garment_type).length > 0 && (
        <section className="mt-16 hairline p-6" data-testid="retag-panel" style={{ borderColor: "#2563EB" }}>
          <div className="flex items-center gap-2"><Tag size={15} style={{ color: "#2563EB" }} /><span className="label-eyebrow">Categorize your clothing</span></div>
          <h2 className="font-display text-2xl tracking-tight mt-2">Help shoppers find these.</h2>
          <p className="text-sm text-neutral-600 mt-1">
            Give each item a category so it appears under the right chip on the Women / Men / Kids pages.
          </p>
          <div className="mt-4 space-y-2">
            {mine.filter((l) => l.product_type === "apparel" && !l.garment_type).map((l) => (
              <div key={l.id} className="flex items-center gap-3 hairline p-2.5" data-testid={`retag-row-${l.id}`}>
                <img src={l.image_paths?.[0]} className="w-10 h-10 object-cover flex-shrink-0" alt="" />
                <span className="text-sm flex-1 truncate">{l.title}</span>
                <select data-testid={`retag-select-${l.id}`} defaultValue=""
                  onChange={(e) => retag(l.id, e.target.value)}
                  className="hairline p-2 text-sm bg-white max-w-[180px]">
                  <option value="" disabled>Pick category…</option>
                  {garmentTypesFor(l.gender, l.age_group).map((g) => (
                    <option key={g.key} value={g.key}>{g.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-16">
        <div className="flex items-baseline justify-between hairline-b pb-3">
          <h2 className="font-display text-2xl">All your listings</h2>
          <Link to="/sell" className="btn-outline">New listing</Link>
        </div>
        {mine.length === 0 ? (
          <div className="hairline p-10 mt-6 text-center">
            <div className="label-eyebrow">Nothing listed yet</div>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-black/10">
            {mine.map(l => (
              <li key={l.id} className="py-4" data-testid={`listing-row-${l.id}`}>
                <div className="flex items-center gap-4">
                  <Link to={`/product/${l.id}`} className="shrink-0">
                    <div className="w-14 h-14 hairline overflow-hidden">
                      <ImageThumb path={l.image_paths?.[0]} className="w-full h-full object-cover"/>
                    </div>
                  </Link>
                  <div className="min-w-0 flex-1">
                    <Link to={`/product/${l.id}`} className="font-display truncate block">{l.title}</Link>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-neutral-500">{l.gender} · {l.age_group} · {l.product_type}</span>
                      <AuthBadge item={l} compact testid={`row-auth-${l.id}`}/>
                    </div>
                  </div>
                  <div className="text-sm text-neutral-500"><Heart size={12} className="inline"/> {l.like_count}</div>
                  <div className="font-display">${Number(l.price).toFixed(2)}</div>
                  <DescriptionTab description={l.description} testid={`row-desc-tab-${l.id}`}/>
                  <div className="flex flex-col items-start gap-1">
                    <span className="chip" data-active={l.active}>{l.active ? "Active" : "Hidden"}</span>
                    {!l.sold && (
                      <button className="chip hover:bg-black/[0.04]" data-testid={`edit-photos-${l.id}`}
                        onClick={() => editListing(l)}>
                        <ImageIcon size={12} /> Edit
                      </button>
                    )}
                    {!l.sold && (
                      <button className="chip hover:bg-red-50" style={{ color: "#DC2626", borderColor: "#DC2626" }}
                        data-testid={`delete-listing-${l.id}`} onClick={() => removeListing(l)}>
                        <Trash2 size={12} /> Delete
                      </button>
                    )}
                  </div>
                  {l.active && !l.sold && (
                    (l.bump_until && new Date(l.bump_until) > new Date()) ? (
                      <span className="chip" data-active="true" data-testid={`featured-badge-${l.id}`}
                        style={{ borderColor: "#2563EB", color: "#2563EB" }}>
                        <Sparkles size={12} /> Featured · {expiryLabelD(l.bump_until)}
                      </span>
                    ) : (
                      <button className="btn-klein text-[0.62rem]" data-testid={`feature-btn-${l.id}`}
                        onClick={() => setFeatureFor(l.id)}>
                        <Sparkles size={12} /> Feature on homepage · from $2
                      </button>
                    )
                  )}
                </div>
                {(l.feature_views !== undefined || l.feature_likes !== undefined) && (
                  <div className="mt-2 ml-[4.5rem] flex items-center gap-3 flex-wrap text-xs text-neutral-500"
                    data-testid={`feature-analytics-${l.id}`}>
                    <span className="label-eyebrow" style={{ color: "#2563EB" }}>Feature results</span>
                    <span className="inline-flex items-center gap-1" data-testid={`feature-views-${l.id}`}>
                      <Eye size={12} /> {l.feature_views ?? 0} view{(l.feature_views ?? 0) === 1 ? "" : "s"}
                    </span>
                    <span className="inline-flex items-center gap-1" data-testid={`feature-likes-${l.id}`}>
                      <Heart size={12} /> {l.feature_likes ?? 0} like{(l.feature_likes ?? 0) === 1 ? "" : "s"}
                    </span>
                    <span className="text-neutral-400">
                      {(l.bump_until && new Date(l.bump_until) > new Date()) ? "· earned while featured (live)" : "· from your last feature"}
                    </span>
                  </div>
                )}
                {l.auth_status === "flagged" && (
                  <div className="mt-3 hairline p-4" style={{borderColor:"#EA580C"}} data-testid={`proof-panel-${l.id}`}>
                    <div className="label-eyebrow" style={{color:"#EA580C"}}>Better authenticity proof needed</div>
                    <p className="mt-2 text-xs text-neutral-600">
                      {l.auth_report?.notes || "Screening could not confirm the brand from these photos."}
                    </p>
                    {l.auth_report?.missing_proof?.length > 0 && (
                      <ul className="mt-2 text-xs text-neutral-500 space-y-0.5">
                        {l.auth_report.missing_proof.slice(0,3).map((m) => <li key={m}>· {m}</li>)}
                      </ul>
                    )}
                    {proofFor === l.id ? (
                      <div className="mt-3">
                        <PhotoUpload paths={proofPaths} onChange={setProofPaths}
                          cta="Add clearer tag / label close-ups" testid={`rescreen-upload-${l.id}`}/>
                        <div className="mt-2 flex gap-2">
                          <button className="btn-klein text-[0.62rem]" onClick={()=>rescreen(l.id)}
                            data-testid={`rescreen-submit-${l.id}`}>Re-submit for screening</button>
                          <button className="chip" onClick={()=>{setProofFor(null); setProofPaths([]);}}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button className="btn-outline text-[0.62rem]" data-testid={`rescreen-btn-${l.id}`}
                          onClick={()=>{setProofFor(l.id); setProofPaths([]);}}>Add proof &amp; re-screen</button>
                        <Link to="/proof-guide" className="text-xs underline" data-testid={`proof-guide-link-${l.id}`}>
                          See example proof photos →
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-16">
        <div className="flex items-baseline justify-between hairline-b pb-3">
          <h2 className="font-display text-2xl">Sales &amp; payouts</h2>
          <span className="label-eyebrow">3-day authenticity cover</span>
        </div>
        <SellerSales />
      </section>

      {featureFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="feature-modal"
          onClick={() => setFeatureFor(null)}>
          <div className="bg-white hairline max-w-md w-full p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} style={{ color: "#1E3A2B" }} />
              <span className="label-eyebrow">Feature this listing</span>
            </div>
            <h2 className="font-display text-2xl tracking-tight mt-3">Get seen faster.</h2>
            <p className="mt-2 text-sm text-neutral-600 leading-relaxed">
              Put your item in the big cover spots at the top of the homepage (and the “For Sale” grid) so it's the
              first thing shoppers see. Pick how long:
            </p>
            <div className="mt-4">
              <BumpTierPicker listingId={featureFor} tiers={boostTiers}
                onCancel={() => setFeatureFor(null)} cancelLabel="Cancel" testidPrefix="feature" />
            </div>
            <p className="mt-3 text-[0.62rem] text-neutral-400 text-center">Secure Stripe checkout.</p>
          </div>
        </div>
      )}
    </div>
  );
}
